import { useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import Button from "@ingka/button";
import arrowCounterclockwise from "@ingka/ssr-icon/paths/arrow-counterclockwise";
import styles from "./StatsCard.module.css";

interface StatsCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    trend?: "up" | "down" | "neutral";
    tooltip?: string;
    loading?: boolean;
    error?: boolean;
    onRetry?: () => void;
    onClick?: () => void;
}

const TOOLTIP_AUTO_CLOSE_MS = 4000;

export function StatsCard({ title, value, subtitle, trend, tooltip, loading, error, onRetry, onClick }: StatsCardProps) {
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        if (!showTooltip) return;
        const id = setTimeout(() => setShowTooltip(false), TOOLTIP_AUTO_CLOSE_MS);
        return () => clearTimeout(id);
    }, [showTooltip]);

    return (
        <div
            className={`${styles.card} ${onClick ? styles.clickable : ""}`}
            title={tooltip || (subtitle ? `${title}: ${subtitle}` : title)}
            onClick={() => {
                if (onClick) {
                    onClick();
                } else if (tooltip) {
                    setShowTooltip((v) => !v);
                }
            }}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                if (onClick && (event.key === "Enter" || event.key === " ")) {
                    event.preventDefault();
                    onClick();
                }
            }}
            role={onClick ? "button" : undefined}
            tabIndex={onClick ? 0 : undefined}
        >
            <div className={styles.title}>{title}</div>
            <div className={styles.value}>
                {loading ? (
                    <span className={styles.spinner} />
                ) : error ? (
                    <span className={styles.errorValue}>
                        —
                        {onRetry && (
                            <Button
                                type="tertiary"
                                size="xsmall"
                                iconOnly
                                ssrIcon={arrowCounterclockwise}
                                className={styles.retryBtn}
                                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation();
                                    onRetry();
                                }}
                                aria-label="Retry"
                            />
                        )}
                    </span>
                ) : (
                    <>
                        {trend && <span className={styles[trend]}>{trend === "up" ? "↑" : trend === "down" ? "↓" : ""}</span>}
                        {typeof value === "number" ? value.toLocaleString() : value}
                    </>
                )}
            </div>
            {!loading && !error && subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            {error && <div className={styles.errorSubtitle}>Failed to load</div>}
            {showTooltip && tooltip && <div className={styles.tooltipBubble}>{tooltip}</div>}
        </div>
    );
}
